
import { Link } from 'react-router-dom';

export default function PsychologicalReactancePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-600 via-gray-600 to-zinc-600 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link to="/motivation" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
              <i className="ri-arrow-left-line mr-2"></i>
              動機付け一覧に戻る
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl mb-6">
              <i className="ri-shield-cross-line text-4xl"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">心理的リアクタンス理論</h1>
            <p className="text-xl text-white/90">Psychological Reactance</p>
          </div>
        </div>
      </section>

      {/* Core Message */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-500 to-gray-500 rounded-2xl shadow-2xl p-8 md:p-12 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">自由を奪われると、人は反発・無関心になる</h2>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* 理論の概要 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-slate-500 to-gray-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-book-open-line text-xl text-white"></i>
                </div>
                理論の概要
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                心理的リアクタンスとは、自分の自由が脅かされたり制限されたりすると、その自由を取り戻そうとする心理的な反発のことです。「〇〇しなさい」と言われると、逆にやりたくなくなる現象です。
              </p>
              <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-slate-200">
                <h3 className="font-bold text-gray-900 mb-3">リアクタンスが起きる場面</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-slate-100">
                    <h4 className="font-bold text-gray-900 mb-2 text-sm">強制・命令</h4>
                    <p className="text-xs text-gray-600">「必ず〇〇しなさい」と言われる</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-100">
                    <h4 className="font-bold text-gray-900 mb-2 text-sm">選択肢の制限</h4>
                    <p className="text-xs text-gray-600">「これしかダメ」と選択肢を狭められる</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-zinc-100">
                    <h4 className="font-bold text-gray-900 mb-2 text-sm">過度な監視</h4>
                    <p className="text-xs text-gray-600">細かくチェックされ、信頼されていないと感じる</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-slate-100">
                    <h4 className="font-bold text-gray-900 mb-2 text-sm">説明なしの指示</h4>
                    <p className="text-xs text-gray-600">理由を説明されずに指示される</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 薬局運営への活用 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-slate-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-hospital-line text-xl text-white"></i>
                </div>
                薬局運営への活用
              </h2>
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-slate-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-error-warning-line text-slate-600 mr-2"></i>
                    指示・管理強化の副作用チェック
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    問題が起きたとき、「もっと管理を強化しよう」「もっと細かく指示しよう」と考えがちですが、これが逆効果になることがあります。
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-red-100">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm flex items-center">
                        <i className="ri-close-circle-line text-red-500 mr-2"></i>
                        リアクタンスを引き起こす対応
                      </h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>・「絶対に〇〇しなさい」</li>
                        <li>・細かすぎるチェック</li>
                        <li>・理由を説明しない指示</li>
                        <li>・選択肢を与えない</li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-green-100">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm flex items-center">
                        <i className="ri-checkbox-circle-line text-green-500 mr-2"></i>
                        リアクタンスを防ぐ対応
                      </h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>・「〇〇してもらえると助かる」</li>
                        <li>・信頼を示す</li>
                        <li>・理由を丁寧に説明</li>
                        <li>・選択肢を与える</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-zinc-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-user-heart-line text-gray-600 mr-2"></i>
                    自律性を壊さないマネジメント
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    職員の自律性を尊重しながら、必要な管理を行うバランスが重要です。
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start p-3 bg-white rounded-lg border border-gray-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <div>
                        <span className="text-gray-700 text-sm font-semibold">「なぜ」を説明する</span>
                        <p className="text-xs text-gray-500">指示の背景や目的を伝える</p>
                      </div>
                    </div>
                    <div className="flex items-start p-3 bg-white rounded-lg border border-gray-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <div>
                        <span className="text-gray-700 text-sm font-semibold">選択肢を与える</span>
                        <p className="text-xs text-gray-500">「AとBどちらがいい？」と選ばせる</p>
                      </div>
                    </div>
                    <div className="flex items-start p-3 bg-white rounded-lg border border-gray-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <div>
                        <span className="text-gray-700 text-sm font-semibold">依頼形で伝える</span>
                        <p className="text-xs text-gray-500">「〇〇してください」→「〇〇してもらえますか？」</p>
                      </div>
                    </div>
                    <div className="flex items-start p-3 bg-white rounded-lg border border-gray-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <div>
                        <span className="text-gray-700 text-sm font-semibold">意見を聞く</span>
                        <p className="text-xs text-gray-500">「どう思う？」と職員の意見を求める</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 言い換え例 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-slate-500 to-gray-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-exchange-line text-xl text-white"></i>
                </div>
                言い換え例
              </h2>
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-red-50 to-green-50 rounded-xl p-4 border border-gray-200">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <i className="ri-close-circle-line text-red-500 mr-2"></i>
                      <span className="text-sm text-gray-700">「必ずこうしなさい」</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-right-line text-gray-400 mr-2 hidden md:block"></i>
                      <i className="ri-checkbox-circle-line text-green-500 mr-2"></i>
                      <span className="text-sm text-gray-700">「こうしてもらえると助かります」</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-red-50 to-green-50 rounded-xl p-4 border border-gray-200">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <i className="ri-close-circle-line text-red-500 mr-2"></i>
                      <span className="text-sm text-gray-700">「これしかダメ」</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-right-line text-gray-400 mr-2 hidden md:block"></i>
                      <i className="ri-checkbox-circle-line text-green-500 mr-2"></i>
                      <span className="text-sm text-gray-700">「AとBどちらがいいですか？」</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-red-50 to-green-50 rounded-xl p-4 border border-gray-200">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <i className="ri-close-circle-line text-red-500 mr-2"></i>
                      <span className="text-sm text-gray-700">「とにかくやって」</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-right-line text-gray-400 mr-2 hidden md:block"></i>
                      <i className="ri-checkbox-circle-line text-green-500 mr-2"></i>
                      <span className="text-sm text-gray-700">「〇〇という理由でお願いしたいのですが」</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 明日の一手 */}
            <div className="bg-gradient-to-br from-slate-600 via-gray-600 to-zinc-600 rounded-2xl shadow-2xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <i className="ri-calendar-check-line text-3xl mr-3"></i>
                明日の一手
              </h2>
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                指示を出すとき、「なぜ」を説明し、選択肢を与えることを意識してみましょう。
              </p>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <p className="text-white font-semibold mb-2">具体的なアクション：</p>
                <p className="text-white/90 leading-relaxed">
                  次に指示を出すとき、「〇〇という理由で、△△か□□のどちらかをお願いできますか？」と伝えてみる。
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-8 border-t border-gray-200">
              <Link to="/motivation/emotional-labor" className="flex items-center text-slate-600 hover:text-slate-700 font-semibold transition-colors">
                <i className="ri-arrow-left-line mr-2"></i>
                感情労働
              </Link>
              <Link to="/motivation" className="flex items-center text-slate-600 hover:text-slate-700 font-semibold transition-colors">
                動機付け一覧に戻る
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
